'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Clock, Eye, Hash, Loader2 } from 'lucide-react';
import FallingManatees from './FallingManatees';

interface ImageRevealPhaseProps {
  onGuessSubmit: (guess: number) => void;
  entryId: string | null;
}

const COUNTDOWN_SECONDS = 15;

const DEFAULT_GAME_IMAGE = {
  src: 'https://images.unsplash.com/photo-1672300941404-8218817c50ba',
  alt: 'Colorful assorted candies in a large glass jar'
};

export default function ImageRevealPhase({ onGuessSubmit, entryId }: ImageRevealPhaseProps) {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [isHiding, setIsHiding] = useState(false);
  const [gameImageSrc, setGameImageSrc] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showGuessForm, setShowGuessForm] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [guess, setGuess] = useState('');
  const [guessError, setGuessError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerStarted = useRef(false);

  // Load image from Supabase
  useEffect(() => {
    let cancelled = false;
    import('@/lib/services/gameImageService').then(({ gameImageService, GUESSING_IMAGE_ID }) => {
      gameImageService.getImage(GUESSING_IMAGE_ID).then((record) => {
        if (cancelled) return;
        setGameImageSrc(record?.imageUrl || DEFAULT_GAME_IMAGE.src);
        setImageLoaded(true);
      });
    });
    return () => { cancelled = true; };
  }, []);

  // Open fullscreen as soon as image is loaded, then start countdown
  useEffect(() => {
    if (!imageLoaded || timerStarted.current) return;
    timerStarted.current = true;
    setFullscreenOpen(true);

    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          setIsHiding(true);
          setTimeout(() => {
            setFullscreenOpen(false);
            setTimeout(() => setShowGuessForm(true), 300);
          }, 300);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [imageLoaded]);

  const handleGuessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseInt(guess, 10);
    if (isNaN(parsed) || parsed < 0) {
      setGuessError('Please enter a valid number');
      return;
    }
    if (parsed > 99999) {
      setGuessError('Guess must be under 100,000');
      return;
    }
    setIsSubmitting(true);
    onGuessSubmit(parsed);
  };

  const progressPercent = (COUNTDOWN_SECONDS - secondsLeft) / COUNTDOWN_SECONDS * 100;
  const isUrgent = secondsLeft <= 5;

  // Show guess form after image disappears
  if (showGuessForm) {
    return (
      <div className="animate-fade-in">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-400 rounded-2xl shadow-lg mb-3">
            <Hash size={28} className="text-yellow-900" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-800">What&apos;s Your Guess?</h2>
          <p className="text-gray-500 text-sm mt-1">
            How many manatees did you count in the image?
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-green-100 p-6">
          <form onSubmit={handleGuessSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="guess-input">
                Your Number Guess <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-500">
                  <Hash size={16} />
                </div>
                <input
                  id="guess-input"
                  type="number"
                  placeholder="e.g. 247"
                  min={0}
                  max={99999}
                  value={guess}
                  onChange={(e) => { setGuess(e.target.value); setGuessError(''); }}
                  autoFocus
                  className={`w-full pl-9 pr-4 py-3 rounded-xl border text-sm font-medium transition-colors duration-150 outline-none focus:ring-2 focus:ring-yellow-400/30 font-tabular ${
                    guessError
                      ? 'border-red-300 bg-red-50' : 'border-yellow-200 bg-yellow-50 focus:border-yellow-400 focus:bg-white'
                  }`}
                />
              </div>
              {guessError && (
                <p className="mt-1 text-xs text-red-500 font-medium">{guessError}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 active:scale-95 text-white font-bold text-base py-4 rounded-2xl shadow-lg shadow-green-200 transition-all duration-150 flex items-center justify-center gap-2"
              style={{ minHeight: '56px' }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <span>Submit My Guess 🎯</span>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Loading state
  if (!imageLoaded) {
    return (
      <div className="animate-fade-in bg-white rounded-3xl shadow-xl border border-green-100 p-8 text-center">
        <Loader2 size={32} className="animate-spin text-green-400 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading game image...</p>
      </div>
    );
  }

  // Fullscreen countdown overlay
  if (fullscreenOpen) {
    return (
      <div
        className={`fixed inset-0 z-50 bg-black flex flex-col items-center justify-center transition-all duration-300 ${
          isHiding ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
        }`}
      >
        {/* Falling manatees distraction */}
        <FallingManatees active={true} />

        {/* Timer overlay at top */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4">
          <div className="flex items-center justify-between mb-2 max-w-2xl mx-auto">
            <div className="flex items-center gap-2">
              <Clock size={16} className={isUrgent ? 'text-red-400' : 'text-white/80'} />
              <span className="text-sm font-medium text-white/80">Time remaining</span>
            </div>
            <span
              className={`text-3xl font-extrabold font-tabular transition-colors duration-300 ${
                isUrgent ? 'text-red-400 animate-pulse' : 'text-white'
              }`}
            >
              {secondsLeft}s
            </span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden max-w-2xl mx-auto">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-linear ${
                isUrgent ? 'bg-red-400' : 'bg-green-400'
              }`}
              style={{ width: `${100 - progressPercent}%` }}
            />
          </div>
        </div>

        {/* Full screen image */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={gameImageSrc}
          alt="Game guessing image - count the manatees"
          className="w-full h-full object-contain"
        />

        {isUrgent && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-full font-bold text-lg shadow-2xl animate-pulse z-10">
            Memorize it! ⚡
          </div>
        )}

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs z-10">
          💡 Count the manatees — you&apos;ll enter your guess after the timer!
        </div>
      </div>
    );
  }

  // Fallback (shouldn't normally show)
  return (
    <div className="animate-fade-in bg-white rounded-3xl shadow-xl border border-green-100 p-8 text-center">
      <Eye size={32} className="text-green-400 mx-auto mb-4" />
      <p className="text-gray-500 text-sm">Preparing...</p>
    </div>
  );
}
